import { forwardRef, useState, useCallback, useEffect, useRef } from "react";
import { isArray, isString, uniqueId } from "lodash";
import SingleInput, {
  SingleInputProps,
  SingleInputRef
} from "@/components/controls/SingleInput";
import Text from "@/components/ui/text";
import AddActionButton from "@/components/ui/add-action-button";
import { useNewFieldsUI } from "@/services/features/useNewFieldsUI";

interface Entry {
  id: string;
  value: string;
}

type OmittedProps =
  | "initialValue"
  | "onSave"
  | "onClickClearButton"
  | "clearOnSave"
  | "customEditButton"
  | "width"
  | "divClassName"
  | "variant";

interface MultiInputProps extends Omit<SingleInputProps, OmittedProps> {
  initialValue?: string[] | string;
  onSave: (value: string[]) => void;
  maxEntries?: number;
  allowAddNew?: boolean;
  addNewText?: string;
  // A required field keeps its last entry: the clear control is withheld rather
  // than disabled, so no variant can route around the gate.
  required?: boolean;
}

const parseInitialValue = (value?: string[] | string): Entry[] => {
  let parsedValues: string[] = [];
  if (isString(value)) {
    try {
      const parsed = JSON.parse(value);
      parsedValues = isArray(parsed) ? parsed : [];
    } catch {
      parsedValues = value
        .split(",")
        .map((entryValue) => entryValue.trim())
        .filter(Boolean);
    }
  } else {
    parsedValues = isArray(value) ? value : [];
  }
  return parsedValues.map((entryValue) => ({
    id: uniqueId("entry-"),
    value: entryValue
  }));
};

const MultiInput = forwardRef<HTMLDivElement, MultiInputProps>(
  (
    {
      fieldName,
      initialValue = [],
      onSave,
      maxEntries = 10,
      allowAddNew = true,
      addNewText,
      required = false,
      readOnly = false,
      textVariant = "body",
      placeholder,
      ...inputProps
    },
    ref
  ) => {
    const isNewFieldsUI = useNewFieldsUI();
    const [entries, setEntries] = useState<Entry[]>(() =>
      parseInitialValue(initialValue)
    );
    const isInitializing = useRef(true);
    const inputRefs = useRef<Record<string, SingleInputRef | null>>({});

    useEffect(() => {
      isInitializing.current = false;
    });

    const handleEntryChange = useCallback(
      (entryId: string, newValue: string) => {
        // Next list computed here rather than inside the updater. React may
        // defer an updater, so a value smuggled out of one through a closure is
        // not reliably set by the time the write would be issued — and a write
        // issued inside it would be sent twice on a replay.
        const entryIndex = entries.findIndex((entry) => entry.id === entryId);
        if (entryIndex === -1) return;

        const nextEntries = !newValue
          ? entries.filter((entry) => entry.id !== entryId)
          : entries.map((entry) =>
              entry.id === entryId ? { ...entry, value: newValue } : entry
            );

        // Not `!newValue ||`: saveUnchangedEmpty routes an untouched draft's
        // blur through here with "" on a row already holding "", which issued a
        // write of the list exactly as stored. The row still disappears —
        // setEntries filters it either way — and a cleared real entry still
        // differs from "" and still saves.
        const changed = entries[entryIndex].value !== newValue;
        setEntries(nextEntries);
        if (changed && !isInitializing.current) {
          onSave(nextEntries.map((entry) => entry.value).filter(Boolean));
        }
      },
      [entries, onSave]
    );

    const addEntry = useCallback(() => {
      if (inputProps.disabled) return;
      const newId = uniqueId("entry-");
      setEntries((previousEntries) => [
        ...previousEntries,
        { id: newId, value: "" }
      ]);
      setTimeout(() => inputRefs.current[newId]?.forceStartEditing(), 0);
    }, [inputProps.disabled]);

    const removeEntry = useCallback(
      (entryId: string) => {
        const removedEntry = entries.find((entry) => entry.id === entryId);
        const nextEntries = entries.filter((entry) => entry.id !== entryId);
        setEntries(nextEntries);
        // Same reason the blur path above checks `changed`: an "Add" click
        // creates a `{ value: "" }` draft that was never stored, so clearing
        // that row saves the list exactly as it already is — a redundant write
        // and, on the detail views, a success toast for a change nobody made.
        if (removedEntry?.value) {
          onSave(nextEntries.map((entry) => entry.value).filter(Boolean));
        }
        delete inputRefs.current[entryId];
      },
      [entries, onSave]
    );

    // Every row counts, drafts included: counting only filled ones let a user
    // open several blanks and fill them past maxEntries.
    const canAddMore =
      allowAddNew && !inputProps.disabled && entries.length < maxEntries;
    const canRemove = !required || entries.length > 1;

    if (readOnly) {
      return (
        <div ref={ref} className="flex flex-col gap-1 w-full">
          {entries.length === 0 ? (
            // With no entries the mapped list renders nothing, and the row
            // collapsed to zero height and fell out of line with its label.
            // Same empty treatment the other read-only controls use.
            <div className="px-2 py-1 min-h-9 flex items-center">
              <Text variant="bodyTertiary">{placeholder}</Text>
            </div>
          ) : (
            entries.map((entry) => (
              <div
                key={entry.id}
                className="px-2 py-1 min-h-9 flex items-center"
              >
                <Text variant={textVariant}>{entry.value}</Text>
              </div>
            ))
          )}
        </div>
      );
    }

    const renderEntry = (entry: Entry, entryVariant?: "inline") => (
      <SingleInput
        key={entry.id}
        ref={(inputElement) => (inputRefs.current[entry.id] = inputElement)}
        fieldName={`${fieldName}_${entry.id}`}
        initialValue={entry.value}
        onSave={(newValue) => handleEntryChange(entry.id, newValue)}
        // Removing a row is a save of "", including for a row opened and left
        // untouched, so this entry's blur must reach onSave even unchanged.
        saveUnchangedEmpty
        onClickClearButton={canRemove ? () => removeEntry(entry.id) : undefined}
        showClearButton={canRemove}
        textVariant={textVariant}
        variant={entryVariant}
        placeholder={placeholder}
        className="w-full"
        containerClassName="w-full"
        editModeContainerClassName="w-full"
        {...inputProps}
        // After the spread, and merged rather than replacing: a required field's
        // last entry has nowhere to fall back to, so clearing it has to fail
        // validation and say why. Letting the save through and dropping it left
        // the row blank while the stored value stood.
        validation={
          canRemove
            ? inputProps.validation
            : { ...(inputProps.validation ?? {}), required: true }
        }
      />
    );

    const renderLegacy = () => (
      <div ref={ref} className="flex flex-col gap-2 w-full">
        {entries.map((entry) => (
          <div key={entry.id} className="flex items-start gap-2">
            <div className="flex-1 w-full">{renderEntry(entry)}</div>
          </div>
        ))}
        {canAddMore && (
          <AddActionButton
            label={addNewText ?? placeholder ?? ""}
            onClick={addEntry}
            type="button"
            className="h-fit w-fit self-start gap-1"
          />
        )}
      </div>
    );

    const renderNew = () => (
      <div ref={ref} className="flex flex-col gap-1 w-full">
        {entries.map((entry) => renderEntry(entry, "inline"))}
        {canAddMore && (
          <AddActionButton
            label={addNewText ?? placeholder ?? ""}
            onClick={addEntry}
            type="button"
            className="h-fit w-fit self-start gap-1"
          />
        )}
      </div>
    );

    return isNewFieldsUI ? renderNew() : renderLegacy();
  }
);

MultiInput.displayName = "MultiInput";

export default MultiInput;
